import type { ReactNode } from "react";
import type { User } from "../types";
import { Brand } from "./Brand";
import {
  DeviceIcon,
  HistoryIcon,
  LogOutIcon,
  SafetyIcon,
  SessionIcon
} from "./Icons";

export type PageName = "session" | "devices" | "history" | "safety";

const navigation = [
  { page: "session" as const, label: "Session", Icon: SessionIcon },
  { page: "devices" as const, label: "Devices", Icon: DeviceIcon },
  { page: "history" as const, label: "History", Icon: HistoryIcon },
  { page: "safety" as const, label: "Safety", Icon: SafetyIcon }
];

export function AppShell({
  user,
  page,
  setPage,
  onLogout,
  children
}: {
  user: User;
  page: PageName;
  setPage: (page: PageName) => void;
  onLogout: () => void;
  children: ReactNode;
}) {
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Brand />
        <nav aria-label="Main navigation">
          {navigation.map(({ page: destination, label, Icon }) => (
            <button
              key={destination}
              className={page === destination ? "active" : ""}
              onClick={() => setPage(destination)}
              aria-current={page === destination ? "page" : undefined}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar__profile">
          <span className="avatar">{user.displayName.slice(0, 1).toUpperCase()}</span>
          <span>
            <strong>{user.displayName}</strong>
            <small>{user.role === "wearer" ? "Wearer" : "Controller"}</small>
          </span>
          <button className="icon-button" onClick={onLogout} aria-label="Sign out">
            <LogOutIcon />
          </button>
        </div>
      </aside>
      <header className="mobile-header">
        <Brand compact />
        <span className="mobile-header__role">
          <span className="status-dot" />
          {user.role === "wearer" ? "Wearer" : "Controller"}
        </span>
        <button className="icon-button" onClick={onLogout} aria-label="Sign out">
          <LogOutIcon />
        </button>
      </header>
      <div className="app-content">{children}</div>
      <nav className="mobile-nav" aria-label="Mobile navigation">
        {navigation.map(({ page: destination, label, Icon }) => (
          <button
            key={destination}
            className={page === destination ? "active" : ""}
            onClick={() => setPage(destination)}
          >
            <Icon />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
